import {
  DefaultError,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from "@tanstack/react-query";
import { useInfiniteHandler, useQueryToast } from "hooks";
import i18next from "i18next";
import {
  IInfinitePageParams,
  IInfinitePageStatisticsResponse,
} from "services/types";
import { apiClient } from "utils";
import { getDefaultInfinitePageParam } from "utils/service/defaultInfinitePageParam";
import { useResponseHandler } from "utils/service/responseHandler";
import {
  IAdvance,
  IAdvanceByLabelResponseInfinite,
  IAdvanceRepayment,
  IAdvanceStatistics,
  IAdvanceUpdateRequest,
  IRepaymentFormData,
  IUnpaidAdvance,
  IUnpaidAdvanceStatistic,
} from "./types";

export const useGetAdvanceDetail = (id: string) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IAdvance, DefaultError>({
    queryKey: ["get-advance-detail", id],
    async queryFn() {
      const response = await apiClient({
        method: "GET",
        url: `Advance/${id}`,
      });

      return responseHandler(response, {
        groupName: `useGetAdvanceDetail`,
      });
    },
  });
};

export const useDeleteAdvanceDetail = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast("useDeleteAdvanceDetail");

  return useMutation<boolean, DefaultError, string>({
    mutationFn: async (id: string) => {
      loading(
        i18next.t("service-message.remove.loading", {
          what: i18next.t("advance.what"),
        })
      );

      const response = await apiClient({
        method: "DELETE",
        url: "Advance",
        body: { advanceId: id },
      });

      return responseHandler(response, {
        groupName: `useDeleteAdvanceDetail`,
      });
    },

    onSuccess: () => {
      success(
        i18next.t("service-message.remove.success", {
          what: i18next.t("advance.what"),
        })
      );
    },
    onError: dismiss,
  });
};

export const usePutRepayment = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast("usePutRepayment");

  return useMutation<{value: string}, DefaultError, IAdvanceRepayment>({
    mutationFn: async (body) => {
      loading(
        i18next.t("service-message.toRepay.loading", {
          what: i18next.t("button.advance"),
        })
      );

      const response = await apiClient({
        method: "PUT",
        url: `Advance/Repayment/Income`,
        body,
      });

      return responseHandler(response, {
        groupName: `usePutRepayment`,
      });
    },
    onSuccess: (data) => {
      success(
        i18next.t("service-message.toRepay.success", {
          what: i18next.t("button.advance"),
        })
      );
    },
    onError: dismiss,
  });
};

export const usePostAdvance = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast("usePostAdvance");

  return useMutation<{}, DefaultError, IRepaymentFormData>({
    mutationFn: async (body) => {
      loading(
        i18next.t("service-message.add.loading", {
          what: i18next.t("label.advance.what"),
        })
      );

      const response = await apiClient({
        method: "POST",
        url: "Advance",
        body: body,
      });

      return responseHandler(response, {
        groupName: "usePostAdvance",
        notFoundPath: "/label",
      });
    },
    onSuccess: (data) => {
      success(
        i18next.t("service-message.upload.success", {
          what: i18next.t("label.tabs.advance"),
        })
      );
    },
    onError: dismiss,
  });
};

export const useUpdateAdvance = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast("useUpdateAdvance");

  return useMutation<{}, DefaultError, IAdvanceUpdateRequest>({
    mutationFn: async (body) => {
      loading(
        i18next.t("service-message.update.loading", {
          what: i18next.t("advance.what"),
        })
      );

      const response = await apiClient({
        method: "PUT",
        url: "Advance",
        body: body,
      });

      return responseHandler(response, {
        groupName: "useUpdateAdvance",
        notFoundPath: "/label",
      });
    },
    onSuccess: (data) => {
      success(
        i18next.t("service-message.update.success", {
          what: i18next.t("advance.what"),
        })
      );
    },
    onError: dismiss,
  });
};

export const useGetAdvancesStatisticsByLabel = (labelId: string) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IAdvanceStatistics, DefaultError>({
    queryKey: ["advances-label-stats", labelId],
    async queryFn() {
      const response = await apiClient({
        method: "GET",
        url: `Advance/Label`,
        params: {
          itemCount: 1,
          page: 0,
          labelId,
        },
      });

      const value = await responseHandler<IAdvanceByLabelResponseInfinite>(
        response,
        {
          groupName: `useGetAdvancesStatisticsByLabel - ${labelId}`,
          notFoundPath: "/label",
        }
      );

      return value.statistics;
    },
  });
};

export const useGetAdvancesByLabelInfinite = (
  labelId: string,
  params?: IInfinitePageParams
) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    params?.page,
    params?.itemCount
  );

  return useInfiniteQuery<IAdvance[], DefaultError>({
    queryKey: ["advances-label-infinite", labelId, params],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: "GET",
        url: `Advance/Label`,
        params: {
          ...params,
          itemCount,
          page: pageParam,
          labelId,
        },
      });

      const value = await responseHandler<IAdvanceByLabelResponseInfinite>(
        response,
        {
          groupName: `useGetAdvancesByLabelInfinite - ${labelId}`,
          notFoundPath: "/label",
        }
      );

      return infiniteHandler<IAdvance>(value);
    },

    initialPageParam: page,
    getNextPageParam: getNextPageParam,
  });
};

export const useGetUnpaidAdvancesStatistics = () => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IUnpaidAdvanceStatistic, DefaultError>({
    queryKey: ["unpaid-advances-stats"],
    async queryFn() {
      const response = await apiClient({
        method: "GET",
        url: `Advance/Unpaid`,
        params: {
          itemCount: 1,
          page: 0,
        },
      });

      const value = await responseHandler<
        IInfinitePageStatisticsResponse<IUnpaidAdvance, IUnpaidAdvanceStatistic>
      >(response, {
        groupName: `useGetUnpaidAdvancesStatistics`,
      });

      return value.statistics;
    },
  });
};

export const useGetUnpaidAdvances = (params?: IInfinitePageParams) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    params?.page,
    params?.itemCount
  );

  return useInfiniteQuery<IUnpaidAdvance[], DefaultError>({
    queryKey: ["unpaid-advances-infinite", params],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: "GET",
        url: `Advance/Unpaid`,
        params: {
          ...params,
          itemCount,
          page: pageParam,
        },
      });

      const value = await responseHandler<
        IInfinitePageStatisticsResponse<IUnpaidAdvance, IUnpaidAdvanceStatistic>
      >(response, {
        groupName: `useGetUnpaidAdvances`,
      });

      return infiniteHandler<IUnpaidAdvance>(value);
    },

    initialPageParam: page,
    getNextPageParam: getNextPageParam,
  });
};

export const useGetHistoricalAdvances = (LabelId: string) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(0, 15);

  return useInfiniteQuery<IAdvance[], DefaultError>({
    queryKey: ["historical-advances", LabelId],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: "GET",
        url: `Advance/Historical`,
        params: {
          LabelId,
          itemCount,
          page: pageParam,
        },
      });

      const value = await responseHandler<IAdvanceByLabelResponseInfinite>(
        response,
        {
          groupName: `useGetHistoricalAdvances - ${LabelId}`,
        }
      );

      return infiniteHandler<IAdvance>(value);
    },

    initialPageParam: page,
    getNextPageParam: getNextPageParam,
  });
};

export const useGetAdvanceReport = (labelId: string, enabled?: boolean) => {
  const { responseStreamHandler } = useResponseHandler();

  return useQuery<Blob, DefaultError>({
    queryKey: ["advance-report", labelId],
    async queryFn() {
      const response = await apiClient({
        method: "GET",
        url: "Advance/Report",
        params: { labelId },
      });

      return await responseStreamHandler(response);
    },
    enabled,
  });
};
